<div class="dialog-header">
  <h2 class="dialog-title" [title]="fileName">{{ fileName || 'Document Preview' }}</h2>
  <div class="header-actions">
    <button mat-icon-button (click)="openInNewTab()" matTooltip="Open in New Tab" class="action-btn">
      <mat-icon>open_in_new</mat-icon>
    </button>
    <button mat-icon-button (click)="download()" matTooltip="Download File" class="action-btn">
      <mat-icon>file_download</mat-icon>
    </button>
    <button mat-icon-button [mat-dialog-close]="true" matTooltip="Close" class="action-btn">
      <mat-icon>close</mat-icon>
    </button>
  </div>
</div>

<mat-dialog-content class="dialog-content">
  <!-- Loading State -->
  <div class="loading-container" *ngIf="loading">
    <div class="spinner"></div>
    <p class="loading-text">Preparing document preview...</p>
  </div>

  <!-- Error State -->
  <div class="error-container" *ngIf="!loading && errorMsg">
    <mat-icon class="error-icon">error_outline</mat-icon>
    <p class="error-text">{{ errorMsg }}</p>
    <button mat-raised-button color="primary" class="error-download-btn" (click)="download()">
      <mat-icon>file_download</mat-icon> Download Document
    </button>
  </div>

  <!-- Document Views -->
  <div class="preview-container" [ngSwitch]="fileType" [hidden]="loading || errorMsg">

    <!-- Image Preview -->
    <div *ngSwitchCase="'image'" class="image-preview">
      <img [src]="url" alt="Image Preview" class="responsive-img" />
    </div>

    <!-- PDF Preview -->
    <div *ngSwitchCase="'pdf'" class="pdf-preview-wrapper">
      <div class="pdf-preview">
        <iframe [src]="safeUrl" class="pdf-iframe"></iframe>
      </div>
      <div class="pdf-footer-bar">
        <span class="pdf-footer-text" [title]="fileName">
          <mat-icon class="pdf-footer-icon">picture_as_pdf</mat-icon>
          {{ fileName }}
        </span>
        <button mat-button class="pdf-footer-link" (click)="openInNewTab()">
          <mat-icon>open_in_new</mat-icon> Open in New Tab
        </button>
      </div>
    </div>

    <!-- Excel Sheet Preview -->
    <div *ngSwitchCase="'excel'" class="excel-preview-wrapper">
      <!-- Toolbar for View Mode Switcher & Graph Banner -->
      <div class="excel-toolbar">
        <div class="excel-mode-buttons">
          <button class="mode-btn" [class.active]="excelViewMode === 'office'" (click)="setExcelViewMode('office')"
            [disabled]="!isPublicExcelUrl"
            [title]="!isPublicExcelUrl ? 'Office View requires standard HTTPS URL (Port 443) without custom ports like :9309' : 'View native Excel layout via MS Office'">
            <mat-icon class="btn-icon">bar_chart</mat-icon>
            <span>Office View (Graphs)</span>
          </button>
          <!-- <button 
            class="mode-btn" 
            [class.active]="excelViewMode === 'google'"
            (click)="setExcelViewMode('google')"
            [disabled]="!isPublicExcelUrl"
            [title]="!isPublicExcelUrl ? 'Google Viewer requires standard HTTPS URL (Port 443) without custom ports like :9309' : 'View document via Google Docs Viewer'">
            <mat-icon class="btn-icon">preview</mat-icon>
            <span>Google View</span>
          </button> -->
          <button class="mode-btn" [class.active]="excelViewMode === 'table'" (click)="setExcelViewMode('table')">
            <mat-icon class="btn-icon">table_chart</mat-icon>
            <span>Grid View (Table)</span>
          </button>
        </div>

        <div class="excel-graph-notice" *ngIf="excelViewMode === 'table'">
          <mat-icon class="notice-icon">info</mat-icon>
          <span *ngIf="hasCustomPortOrHttp">Grid View displays tabular data.</span>
          <span *ngIf="!hasCustomPortOrHttp">Grid view displays tabular data. For Excel graphs, switch to <strong>Office
              View</strong> or download the file.</span>
        </div>
      </div>

      <!-- Office Online Web Viewer Mode (Includes Graphs & Charts) -->
      <div class="office-iframe-container" *ngIf="excelViewMode === 'office' && isPublicExcelUrl">
        <iframe [src]="safeOfficeUrl" class="office-excel-iframe" frameborder="0"></iframe>
      </div>

      <!-- Google Docs Web Viewer Mode -->
      <div class="office-iframe-container" *ngIf="excelViewMode === 'google' && isPublicExcelUrl">
        <iframe [src]="safeGoogleUrl" class="office-excel-iframe" frameborder="0"></iframe>
      </div>

      <!-- SheetJS Grid Table Mode (Fast Tabular View) -->
      <div class="table-view-container" *ngIf="excelViewMode === 'table' || !isPublicExcelUrl">
        <!-- Sheet Tabs selector (shown if workbook has multiple sheets) -->
        <div class="excel-tabs-bar" *ngIf="excelSheets.length > 1">
          <button *ngFor="let sheet of excelSheets; let i = index" class="excel-tab-btn"
            [class.active]="activeSheetIndex === i" (click)="activeSheetIndex = i">
            <mat-icon class="tab-icon">table_chart</mat-icon>
            <span>{{ sheet.name }}</span>
          </button>
        </div>

        <!-- Excel Table Container -->
        <div class="excel-table-scroll-container">
          <table class="excel-rendered-table">
            <tbody>
              <tr *ngFor="let row of excelSheets[activeSheetIndex]?.data; let rIdx = index">
                <td class="excel-row-num">{{ rIdx + 1 }}</td>
                <td *ngFor="let cell of row" class="excel-cell">
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- CSV Preview -->
    <div *ngSwitchCase="'csv'" class="excel-preview-wrapper">
      <div class="excel-table-scroll-container">
        <table class="excel-rendered-table">
          <tbody>
            <tr *ngFor="let row of excelSheets[0]?.data; let rIdx = index">
              <td class="excel-row-num">{{ rIdx + 1 }}</td>
              <td *ngFor="let cell of row" class="excel-cell">
                {{ cell }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Word (DOCX) Preview -->
    <div *ngSwitchCase="'docx'" class="docx-preview-wrapper">
      <div id="docx-preview-container" class="docx-canvas"></div>
    </div>

    <!-- Fallback / Unsupported View -->
    <div *ngSwitchDefault class="fallback-preview">
      <div class="fallback-card">
        <mat-icon class="fallback-icon">insert_drive_file</mat-icon>
        <p class="fallback-text">Preview is not available for this file format.</p>
        <button mat-raised-button color="primary" class="fallback-btn" (click)="download()">
          <mat-icon>file_download</mat-icon> Download Document
        </button>
      </div>
    </div>

  </div>
</mat-dialog-content>